import { useState, useEffect } from 'react';
import { FiEye, FiSearch, FiX } from 'react-icons/fi';
import { apiGetAllUsers, apiGetUserById, apiUpdateUserStatus } from '../api';
import Pagination from '../components/Pagination';
import { confirmAdminAction } from '../utils/adminConfirm';

const UsersManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await apiGetAllUsers({ page, limit: 20, search: debouncedSearch });
        setUsers(data.users || []);
        setPagination(data.pagination || { total: 0, pages: 1 });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [page, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const updateStatus = async (user) => {
    const nextActive = user.isActive === false;
    const confirmed = await confirmAdminAction(
      `${nextActive ? 'Unblock' : 'Block'} ${user.name || user.email}?`,
      { title: `${nextActive ? 'Unblock' : 'Block'} user`, confirmLabel: nextActive ? 'Unblock' : 'Block' }
    );
    if (!confirmed) return;
    try {
      const data = await apiUpdateUserStatus(user.id, nextActive);
      setUsers((current) => current.map((entry) => entry.id === user.id ? data.user : entry));
    } catch { /* API client displays the error toast. */ }
  };

  const viewUser = async (id) => {
    setDetailsLoading(true);
    try { const data = await apiGetUserById(id); setSelectedUser(data.user); } catch { /* API toast */ } finally { setDetailsLoading(false); }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-black" size={16} />
          <input
            type="text"
            placeholder="Search users by name, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:border-orange-500"
          />
        </div>
        <span className="text-sm text-black">{pagination.total} users</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-black">
              <tr>
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-black">#{user.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold text-orange-600">
                        {user.name?.charAt(0).toUpperCase()}
                      </span>
                      <span className="font-medium text-black">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{user.email}</td>
                  <td className="px-4 py-3 text-black">{user.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-black'
                      }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-black">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => viewUser(user.id)} disabled={detailsLoading} className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium hover:bg-gray-50"><FiEye /> View</button>
                    <button
                      type="button"
                      onClick={() => updateStatus(user)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${user.isActive === false ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-red-300 text-red-700 hover:bg-red-50'}`}
                    >
                      {user.isActive === false ? 'Unblock' : 'Block'}
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-black">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={page} pages={pagination.pages} onChange={setPage} />
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setSelectedUser(null)}>
          <aside className="h-full w-full max-w-md overflow-y-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="text-lg font-bold">User Details</h2><button onClick={() => setSelectedUser(null)} aria-label="Close"><FiX /></button></div>
            <dl className="mt-5 space-y-3 text-sm"><div><dt className="text-gray-500">Name</dt><dd className="font-medium">{selectedUser.name}</dd></div><div><dt className="text-gray-500">Email</dt><dd className="break-all font-medium">{selectedUser.email}</dd></div><div><dt className="text-gray-500">Phone</dt><dd>{selectedUser.phone || '—'}</dd></div><div><dt className="text-gray-500">Role</dt><dd className="capitalize">{selectedUser.role}</dd></div><div><dt className="text-gray-500">Status</dt><dd>{selectedUser.isActive === false ? 'Blocked' : 'Active'}</dd></div></dl>
            <h3 className="mt-6 font-semibold">Saved Addresses</h3>
            <div className="mt-2 space-y-3">{(selectedUser.addresses || []).map((address) => <article key={address.id} className="rounded-lg border p-3 text-sm"><p className="font-medium">{address.name} {address.isDefault ? '· Default' : ''}</p><p className="mt-1 text-gray-600">{[address.line1, address.line2, address.city, address.state, address.pincode, address.country].filter(Boolean).join(', ')}</p><p className="mt-1">{address.phone}</p></article>)}{!selectedUser.addresses?.length && <p className="text-sm text-gray-500">No saved addresses.</p>}</div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default UsersManager;
